import { useEffect, useState } from 'react';
import { endpoints } from '../../../api';
import type { SpeechOrder } from '../../../api/speech';

export interface OrderImage {
  index: number;
  url: string;
  name: string;
  mime: string;
  blob: Blob;
}
/** Кадры итога только через owner-protected endpoint заказа. В отличие от общего usePhotoGallery (до 4 вариантов «Фото»)
    фотосессии и наборы товара отдают до 12 кадров, а свои фото сцен — до 9 · N. */
export function useOrderImages(order: SpeechOrder | null, limit = 36) {
  const [state, setState] = useState<{ identity: string; items: OrderImage[]; error: boolean }>({ identity: '', items: [], error: false });
  const [attempt, retry] = useState(0);
  const identity = order?.status === 'done' && order.result?.available ? `${order.id}:${order.result_revision}:${attempt}` : '';
  useEffect(() => {
    if (!identity || !order?.result) return;
    const request = new AbortController();
    const created: string[] = [];
    let disposed = false;
    const timer = setTimeout(() => request.abort(), 60000);
    void (async () => {
      const assets = order.result!.assets.filter(asset => /^image\/(jpeg|png|webp)$/.test(asset.mime));
      if (!assets.length || assets.length > limit) throw new Error('order_result_invalid');
      const items: OrderImage[] = [];
      for (const asset of assets) {
        const blob = await endpoints.speech.result(order, asset.index, request.signal);
        if (disposed || request.signal.aborted) return;
        if (!/^image\/(jpeg|png|webp)$/.test(blob.type) || blob.size > 30 * 1024 * 1024) throw new Error('order_result_invalid');
        const url = URL.createObjectURL(blob);
        created.push(url);
        items.push({ index: asset.index, url, name: asset.file_name, mime: blob.type, blob });
      }
      if (!disposed && !request.signal.aborted) setState({ identity, items, error: false });
    })()
      .catch(() => {
        created.splice(0).forEach(url => URL.revokeObjectURL(url));
        if (!disposed) setState({ identity, items: [], error: true });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      disposed = true;
      request.abort();
      clearTimeout(timer);
      created.forEach(url => URL.revokeObjectURL(url));
    };
  }, [identity, order, limit]);
  return {
    items: state.identity === identity ? state.items : [],
    loading: !!identity && state.identity !== identity,
    error: !!identity && state.identity === identity && state.error,
    retry: () => retry(value => value + 1),
  };
}
